import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Radio,
  message,
  Divider,
  Switch
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  AndroidOutlined,
  AppleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchApplication,
  createApplication,
  updateApplication,
  selectCurrentApplication,
  selectApplicationsLoading,
} from 'store/slices/applications.slice';
import { AppType, ApplicationCreateRequest, FirebaseConfig } from 'types/application.types';
import { formRules, validators } from 'utils/validators';
import { ROUTES, generatePath } from 'config/routes';
import '../../styles/Common.css';

const { TextArea } = Input;

const ApplicationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const application = useAppSelector(selectCurrentApplication);
  const isLoading = useAppSelector(selectApplicationsLoading);

  const [appType, setAppType] = useState<AppType>(AppType.ANDROID_IOS);
  const [firebaseConfigText, setFirebaseConfigText] = useState('');

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchApplication(Number(id)));
    }
  }, [isEditMode, id, dispatch]);

  useEffect(() => {
    if (application && isEditMode) {
      form.setFieldsValue({
        name: application.name,
        bundle_id: application.bundle_id,
        type: application.type,
      });
      setAppType(application.type);
      setFirebaseConfigText(JSON.stringify(application.firebase_config, null, 2));
    }
  }, [application, isEditMode, form]);

  const handleSubmit = async (values: any) => {
    try {
      let firebaseConfig: FirebaseConfig;
      try {
        firebaseConfig = JSON.parse(firebaseConfigText);
      } catch (error) {
        message.error('Неверный формат Firebase конфигурации. Проверьте JSON.');
        return;
      }
      if (!validators.firebaseConfig(firebaseConfig)) {
        message.error('Firebase конфигурация содержит не все необходимые поля');
        return;
      }

      const data: ApplicationCreateRequest = {
        name: values.name,
        bundle_id: values.bundle_id,
        type: values.type,
        firebase_config: firebaseConfig,
        send_install_data: values.send_install_data ?? false,
      };

      if (isEditMode && application) {
        await dispatch(
          updateApplication({
            id: application.id,
            data: {
              name: data.name,
              firebase_config: data.firebase_config,
            },
          })
        ).unwrap();
        message.success('Приложение успешно обновлено');
        navigate(generatePath(ROUTES.APPLICATION_DETAIL, { id: application.id }));
      } else {
        const result = await dispatch(createApplication(data)).unwrap();
        message.success('Приложение успешно создано');
        navigate(generatePath(ROUTES.APPLICATION_DETAIL, { id: result.application.id }));
      }
    } catch (error: any) {
      message.error(error?.message || 'Произошла ошибка при сохранении');
    }
  };

  const handleCancel = () => {
    if (isEditMode && application) {
      navigate(generatePath(ROUTES.APPLICATION_DETAIL, { id: application.id }));
    } else {
      navigate(ROUTES.APPLICATIONS);
    }
  };

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <div className="page-header-content">
            <Space>
              <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleCancel} />
              <div>
                <h1>{isEditMode ? 'Редактирование приложения' : 'Новое приложение'}</h1>
                <p>
                  {isEditMode
                    ? 'Изменение параметров приложения'
                    : 'Создание нового приложения для отправки push-уведомлений'}
                </p>
              </div>
            </Space>
          </div>
        </div>

        <Divider />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ type: AppType.ANDROID_IOS }}
          style={{ maxWidth: 800 }}
        >
          <div className="form-section">
            <h3 className="form-section-title">Основная информация</h3>

            <Form.Item
              label="Отправка данных об установке"
              name="send_install_data"
              valuePropName="checked"
              extra="Отправлять данные об установке во Памятки"
            >
              <Switch checkedChildren="Включено" unCheckedChildren="Выключено" />
            </Form.Item>
            <Form.Item
              label="Название приложения"
              name="name"
              rules={[
                { required: true, message: 'Введите название приложения' },
                { min: 3, message: 'Название должно содержать минимум 3 символа' },
              ]}
            >
              <Input placeholder="Например: My Awesome App" size="large" />
            </Form.Item>

            <Form.Item
              label="Bundle ID"
              name="bundle_id"
              rules={[
                ...formRules.bundleId,
              ]}
              extra="Уникальный идентификатор приложения (например: com.company.app)"
            >
              <Input
                placeholder="com.company.app"
                size="large"
                disabled={isEditMode}
              />
            </Form.Item>

            <Form.Item
              label="Тип приложения"
              name="type"
              rules={[{ required: true, message: 'Выберите тип приложения' }]}
            >
              <Radio.Group
                onChange={(e) => setAppType(e.target.value)}
                disabled={isEditMode}
                size="large"
              >
                <Radio.Button value={AppType.ANDROID_IOS}>
                  <Space>
                    <AndroidOutlined style={{ color: '#3ddc84' }} />
                    <AppleOutlined />
                    Мобильное (Android/iOS)
                  </Space>
                </Radio.Button>
                <Radio.Button value={AppType.PWA}>
                  <Space>
                    <GlobalOutlined />
                    PWA
                  </Space>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>

          <Divider />

          <div className="form-section">
            <h3 className="form-section-title">Firebase конфигурация</h3>
            <p className="form-section-description">
              Вставьте JSON конфигурацию из Firebase Console (Service Account Key)
            </p>

            <Form.Item
              label="Firebase Service Account JSON"
              required
              help="Файл service account можно скачать в Firebase Console → Project Settings → Service Accounts"
            >
              <TextArea
                value={firebaseConfigText}
                onChange={(e) => setFirebaseConfigText(e.target.value)}
                placeholder={`{
  "type": "service_account",
  "project_id": "your-project-id",
  "private_key_id": "...",
  "private_key": "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----\\n",
  "client_email": "firebase-adminsdk@your-project.iam.gserviceaccount.com",
  "client_id": "...",
  "auth_uri": "https://accounts.google.com/o/oauth2/auth",
  "token_uri": "https://oauth2.googleapis.com/token",
  "bundle": "${appType === AppType.ANDROID_IOS ? 'com.yourcompany.app' : ''}",
  "oc": "${appType === AppType.PWA ? 'your-oc-id' : ''}"
}`}
                rows={15}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </Form.Item>
          </div>

          <Divider />

          <Form.Item>
            <div className="actions-group">
              <Button onClick={handleCancel} size="large">
                Отмена
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={isLoading}
                size="large"
              >
                {isEditMode ? 'Сохранить изменения' : 'Создать приложение'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ApplicationForm;